/**
 * Minimal Server-Sent Events envelope parser for our long-running AI generations
 * (e.g. VSL Creator). The backend keeps the connection alive with heartbeat
 * comments (`: ping …`) so CloudFront never issues a 504, then emits a single
 * envelope on a `data:` line:
 *   { type: 'done', payload: <body> }   — success
 *   { type: 'error', status, message }  — failure
 *
 * This module is intentionally dependency-free (no i18n/fetch/DOM) so it can be
 * unit-tested under the node Vitest harness and reused by `apiRequestSSE`.
 *
 * VP-130: the backend may additionally emit non-terminal progress envelopes
 * ({ type: 'progress', stage }) so long flows (scrape → AI) can drive a staged
 * loading UI. Parsers without an onProgress handler ignore them.
 */

export type SSEEnvelope =
  | { type: "progress"; stage?: string }
  | { type: "done"; payload?: unknown }
  | { type: "error"; status?: number; message?: string };

/** Thrown when the stream delivers an `error` envelope. */
export class SSEStreamError extends Error {
  readonly status: number;
  readonly detail: string;
  constructor(status: number, detail: string) {
    super(`${status}: ${detail}`);
    this.name = "SSEStreamError";
    this.status = status;
    this.detail = detail;
  }
}

/**
 * Incremental parser: feed decoded text chunks via `push()`. Events are framed
 * on the blank-line (`\n\n`) boundary. Heartbeat/comment events (no `data:` line)
 * are ignored; a `done` envelope sets `isDone`/`result`; an `error` envelope
 * throws `SSEStreamError` from `push()`.
 */
export class SSEEnvelopeParser {
  private buffer = "";
  private finished = false;
  private payload: unknown;

  constructor(private readonly onProgress?: (stage: string) => void) {}

  push(chunk: string): void {
    this.buffer += chunk;
    let sepIndex: number;
    while ((sepIndex = this.buffer.indexOf("\n\n")) !== -1) {
      const rawEvent = this.buffer.slice(0, sepIndex);
      this.buffer = this.buffer.slice(sepIndex + 2);
      this.handleEvent(rawEvent);
    }
  }

  private handleEvent(rawEvent: string): void {
    // Comment lines (": ping …") carry no `data:` field — they are heartbeats.
    const dataLines = rawEvent.split("\n").filter((line) => line.startsWith("data:"));
    if (dataLines.length === 0) return;

    const json = dataLines.map((line) => line.slice("data:".length).trim()).join("\n");
    let evt: SSEEnvelope;
    try {
      evt = JSON.parse(json) as SSEEnvelope;
    } catch {
      // Partial/garbled frame — skip; a well-formed envelope will follow.
      return;
    }

    if (evt.type === "progress") {
      if (typeof evt.stage === "string" && evt.stage) this.onProgress?.(evt.stage);
      return;
    }
    if (evt.type === "error") {
      const status = typeof evt.status === "number" ? evt.status : 500;
      const message =
        typeof evt.message === "string" && evt.message ? evt.message : "Błąd serwera.";
      throw new SSEStreamError(status, message);
    }
    if (evt.type === "done") {
      this.payload = (evt as { payload?: unknown }).payload ?? evt;
      this.finished = true;
    }
  }

  get isDone(): boolean {
    return this.finished;
  }

  get result(): unknown {
    return this.payload;
  }
}
